/* eslint-disable no-underscore-dangle */
import React, { useEffect, useRef, useState } from 'react';
import {
  Card, Button, Alert, Badge, ListGroup, Form, Row, Col,
} from 'react-bootstrap';
import {
  apiErrorText, docTypeLabel, formatAmount, PAYMENT_METHODS, DOCUMENT_LANGUAGES, DOC_TYPES,
} from '../../Utils/billingoDocuments';
import BillingoService from '../../Services/BillingoService';
import Error from '../Error';
import { useInvoicePdf } from './openInvoicePdf';
import { finalInvoiceBlockedReason } from '../../Utils/tripClaims';
import FinalInvoiceBlockedAlert from '../TripClaims/FinalInvoiceBlockedAlert';
import BillingoSendModal from './BillingoSendModal';
import BillingoAdvancesCard from './BillingoAdvancesCard';
import BillingoPrefillCard from './BillingoPrefillCard';
import { paymentAllocationGap } from '../../Pages/Payments/Components/paymentBookingAllocations';

// Hint shown after creating a document, nudging the next step in the chain.
const NEXT_HINT = {
  elolegszamla: 'Create the Előlegszámla once payment is received.',
  szamla: 'Create the final Számla after the trip — it deducts any advance.',
};

// Step 4 — review and create. After creation, surfaces the document number +
// PDF link, plus a one-click continue to the next document in the chain. In
// payment mode, also confirms the payment→order assignment.
//
// THE RULE THIS STEP IS BUILT AROUND: say everything that is known before the
// press, because the press is irreversible. Three things it says that it used
// not to, each with its own card and its own measurement in the file that
// decides it:
//   • where the pre-filled values came from, and how the booking has moved
//     since (BillingoPrefillCard / Utils/billingoPrefill.js);
//   • which advances can still be deducted and which are already spent
//     (BillingoAdvancesCard / advanceBlockedReason in billingoDocuments.js);
//   • whether the linked payment carries money this document does not account
//     for, with Edit allocation one click away (paymentAllocationGap).
// Both refusals — the trip-claims one and the spent-advance one — disable the
// button and name their reason in its tooltip, so neither can be clicked past.
function BillingoReviewPanel({
  docType, account = null, totals, orderReferences = [], context,
  creating = false, error = '', createdDoc = null,
  meta = {}, onMetaChange = () => {},
  payments = [], paymentId = null, onSelectPayment = () => {}, onRecordNew = () => {},
  onEditAllocation = null,
  advances = [], advanceBlocked = '', currency = null, bookingIds = [], billedGross = null,
  predecessor = null, prefillSources = {}, drift = null,
  nextType = null, onCreateNext = () => {}, onCreate, onClose,
}) {
  // Tie the invoice to a real payment — only for Számla / Előlegszámla
  // (a Díjbekérő is just a request).
  const canLinkPayment = docType === DOC_TYPES.SZAMLA || docType === DOC_TYPES.ELOLEGSZAMLA;
  const paymentLabel = (p) => {
    const when = (p.occurred_at || '').slice(0, 10);
    return `${formatAmount(p.gross_amount, p.currency)}${when ? ` · ${when}` : ''}`;
  };
  const multiCurrency = totals.currencies.length > 1;
  const customerEmail = context.order?.email_address || '';
  // A final Számla needs a finished booking with nothing waiting for review
  // (the API refuses it too, naming the same bookings).
  const finalInvoiceBookings = docType === DOC_TYPES.SZAMLA
    ? (context.bookings || (context.order ? [context.order] : []))
    : [];
  const finalBlocked = finalInvoiceBlockedReason(finalInvoiceBookings);
  // Same shape for both refusals: one sentence for the disabled button's
  // tooltip, the detail named in the alert above it.
  const blocked = finalBlocked || advanceBlocked;
  // Only a Számla deducts an advance (buildSzamlaWithDeductions is called for
  // that type alone), so only a Számla may say "deducted here". A second
  // Előlegszámla can be chosen by hand over a first, and the card would
  // otherwise claim a deduction that document does not make.
  const deductingAdvances = docType === DOC_TYPES.SZAMLA ? advances : [];

  const selectedPayment = paymentId
    ? payments.find((p) => (p._id || p.id) === paymentId)
    : null;
  // What this document BILLS, before any advance is netted off — NOT
  // `totals`, which is what it charges. A Számla that deducts a 290 advance
  // charges 116 and would make every payment for the trip look like a
  // surplus, so the allocation notice would fire on every advance-netted
  // Számla. The payment belongs against what the trip was billed.
  const allocationGap = paymentAllocationGap(selectedPayment, {
    bookingIds,
    gross: totals.currencies.length === 1 ? billedGross : null,
  });

  const [showSend, setShowSend] = useState(false);
  const [autoSend, setAutoSend] = useState(false);
  const [autoEmail, setAutoEmail] = useState(customerEmail);
  const [autoSentTo, setAutoSentTo] = useState(null);
  const [autoSendError, setAutoSendError] = useState('');
  const autoSentRef = useRef(false);
  const pdf = useInvoicePdf();

  // Auto-send once the document is created, if the toggle was set. A refusal
  // is said on screen — the document exists, the customer just doesn't have
  // it — and "Send to customer" is right there to try again.
  useEffect(() => {
    const recipients = autoEmail.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (createdDoc?.billingo_document_id && autoSend && recipients.length && !autoSentRef.current) {
      autoSentRef.current = true;
      BillingoService.sendDocument({
        billingo_document_id: createdDoc.billingo_document_id,
        emails: recipients,
      })
        .then((res) => setAutoSentTo(res?.sent_to || recipients))
        .catch((err) => setAutoSendError(
          `${docTypeLabel(createdDoc.type)} ${createdDoc.number} was created, but emailing it to `
          + `${recipients.join(', ')} failed: ${apiErrorText(err, 'the send failed.')} `
          + 'Use "Send to customer" to try again.',
        ));
    }
  }, [createdDoc, autoSend, autoEmail]);

  if (createdDoc) {
    return (
      <div>
        <Alert variant="success">
          <Alert.Heading className="h6">
            {docTypeLabel(createdDoc.type)}
            {' '}
            created
          </Alert.Heading>
          <div>
            Number:
            {' '}
            <strong>{createdDoc.number}</strong>
          </div>
          <div>
            Total:
            {' '}
            {formatAmount(createdDoc.total, createdDoc.currency)}
          </div>
          {context.mode === 'payment' && (
            <div className="mt-2">
              <i className="fa fa-check text-success me-1" />
              Payment assigned to order
              {' '}
              {orderReferences[0]}
            </div>
          )}
          <div className="mt-2 d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => pdf.open(createdDoc)}
              disabled={pdf.opening === createdDoc.id}
            >
              <i className="fa fa-file-pdf me-1" />
              {pdf.opening === createdDoc.id ? 'Opening PDF…' : 'Open PDF'}
            </Button>
            {createdDoc.billingo_document_id && (
              <Button variant="outline-primary" size="sm" onClick={() => setShowSend(true)}>
                <i className="fa fa-paper-plane me-1" />
                Send to customer
              </Button>
            )}
          </div>
          {autoSentTo && (
            <div className="mt-2 small text-success">
              <i className="fa fa-check me-1" />
              Emailed to
              {' '}
              {autoSentTo.join(', ')}
            </div>
          )}
        </Alert>

        <Error error={pdf.error} setError={pdf.setError} />
        <Error error={autoSendError} setError={setAutoSendError} />

        <BillingoSendModal
          show={showSend}
          doc={createdDoc}
          defaultEmail={customerEmail}
          onHide={() => setShowSend(false)}
          onSent={() => setAutoSendError('')}
        />

        {nextType && (
          <Alert variant="light" className="d-flex justify-content-between align-items-center">
            <span className="small text-muted">{NEXT_HINT[nextType]}</span>
            <Button variant="outline-primary" size="sm" onClick={onCreateNext}>
              Create
              {' '}
              {docTypeLabel(nextType)}
            </Button>
          </Alert>
        )}

        <Button onClick={onClose}>Done</Button>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}

      <BillingoPrefillCard
        predecessor={predecessor}
        docType={docType}
        sources={prefillSources}
        account={account}
        currency={currency}
        fulfillmentDate={meta.fulfillment_date}
        drift={drift}
      />

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Document</span>
            <Badge bg="primary">{docTypeLabel(docType)}</Badge>
          </div>
          <hr />
          <div className="d-flex justify-content-between">
            <span className="text-muted">Billingo account</span>
            <span className="fw-semibold">{account?.name || '—'}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Order reference(s)</span>
            <span>{orderReferences.join(', ') || '—'}</span>
          </div>
        </Card.Body>
      </Card>

      {multiCurrency && (
        <Alert variant="warning">
          Selected lines mix currencies (
          {totals.currencies.join(', ')}
          ). A Billingo document is single-currency — split the selection per currency.
        </Alert>
      )}

      <BillingoAdvancesCard advances={deductingAdvances} currency={currency} />

      <ListGroup className="mb-3">
        {totals.currencies.map((cur) => (
          <ListGroup.Item key={cur} className="d-flex justify-content-between">
            <span>
              Total (
              {cur}
              )
            </span>
            <span className="fw-semibold">
              {formatAmount(totals.byCurrency[cur].gross, cur)}
              <span className="text-muted small ms-2">
                incl.
                {' '}
                {formatAmount(totals.byCurrency[cur].vat, cur)}
                {' '}
                VAT
              </span>
            </span>
          </ListGroup.Item>
        ))}
        {totals.currencies.length === 0 && (
          <ListGroup.Item className="text-muted text-center">No lines selected.</ListGroup.Item>
        )}
      </ListGroup>

      <Card className="mb-3">
        <Card.Body>
          <div className="text-muted small mb-2">Invoice details</div>
          <Row className="g-2">
            <Col xs={6} md={3}>
              <Form.Label className="small mb-1">Fulfillment date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={meta.fulfillment_date || ''}
                onChange={(e) => onMetaChange({ fulfillment_date: e.target.value })}
              />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="small mb-1">Due date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={meta.due_date || ''}
                onChange={(e) => onMetaChange({ due_date: e.target.value })}
              />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="small mb-1">Payment method</Form.Label>
              <Form.Select
                size="sm"
                value={meta.payment_method || 'wire_transfer'}
                onChange={(e) => onMetaChange({ payment_method: e.target.value })}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="small mb-1">Language</Form.Label>
              <Form.Select
                size="sm"
                value={meta.language || 'en'}
                onChange={(e) => onMetaChange({ language: e.target.value })}
              >
                {DOCUMENT_LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </Form.Select>
            </Col>
            {docType !== DOC_TYPES.DIJBEKERO && (
              <Col xs={6} md={3}>
                <Form.Label className="small mb-1">Payment date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={meta.payment_date || ''}
                  onChange={(e) => onMetaChange({ payment_date: e.target.value })}
                />
              </Col>
            )}
          </Row>
          <div className="text-muted small mt-2">
            Defaults by type
            {docType !== DOC_TYPES.DIJBEKERO && '; payment date is added to the document comment'}
            .
          </div>
        </Card.Body>
      </Card>

      {canLinkPayment && (
        <Card className="mb-3">
          <Card.Body>
            <div className="text-muted small mb-2">Linked payment (optional)</div>
            <div className="d-flex gap-2 align-items-start">
              <Form.Select
                size="sm"
                value={paymentId || ''}
                onChange={(e) => onSelectPayment(e.target.value || null)}
              >
                <option value="">No payment</option>
                {payments.map((p) => (
                  <option key={p._id || p.id} value={p._id || p.id}>{paymentLabel(p)}</option>
                ))}
              </Form.Select>
              <Button variant="outline-secondary" size="sm" className="text-nowrap" onClick={onRecordNew}>
                <i className="fa fa-plus me-1" />
                Record new
              </Button>
            </div>
            {/*
              THE SENTENCE THAT USED TO BE HERE WAS FALSE. It read "Links this
              invoice to a real payment and allocates it across the invoiced
              lines." Selecting a payment only sets `payment_id` on the
              document (handleSelectPayment in BillingoDocumentFlow, which
              also defaults the payment date); nothing writes Payment.lines.
              An operator reading it had been told the split was done.
            */}
            <div className="text-muted small mt-2">
              Links this invoice to a payment. Splitting it across order lines is separate —
              use Edit allocation.
            </div>
            {allocationGap && (
              <Alert variant="warning" className="py-2 mt-2 mb-0" data-testid="payment-allocation-gap">
                <div className="small">
                  {allocationGap.others.length > 0 && (
                    <>
                      {`This payment is also attributed to ${allocationGap.others
                        .map((a) => a.booking_reference || a.booking_id).join(', ')}.`}
                      {' Only its share of this document’s bookings settles this invoice.'}
                    </>
                  )}
                  {allocationGap.unallocated && (
                    <>
                      {`${formatAmount(allocationGap.amount, allocationGap.currency)} was recorded`}
                      {' against this booking with no split across order lines, and this document'}
                      {` bills ${formatAmount(billedGross, totals.currencies[0])} of them.`}
                      {' Allocate it so the rest lands where it belongs — do not record it again.'}
                    </>
                  )}
                </div>
                {onEditAllocation && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mt-2"
                    onClick={() => onEditAllocation(selectedPayment)}
                  >
                    <i className="fa fa-pencil me-1" />
                    Edit allocation
                  </Button>
                )}
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      <Form.Check
        type="checkbox"
        id="billingo-auto-send"
        className="mb-2"
        label="Email this document to the customer after creating"
        checked={autoSend}
        onChange={(e) => setAutoSend(e.target.checked)}
      />
      {autoSend && (
        <Form.Group className="mb-3">
          <Form.Control
            value={autoEmail}
            placeholder="customer@example.com"
            onChange={(e) => setAutoEmail(e.target.value)}
          />
        </Form.Group>
      )}

      <FinalInvoiceBlockedAlert bookings={finalInvoiceBookings} />
      {advanceBlocked && (
        <Alert variant="danger" className="py-2" data-testid="advance-blocked">
          <div className="fw-semibold">
            <i className="fa fa-triangle-exclamation me-1" />
            This Számla cannot be issued yet
          </div>
          <div className="small mt-1">{advanceBlocked}</div>
          <div className="small mt-1 mb-0">
            Invoicing → Failed documents has Billingo&apos;s own words for the refusal.
          </div>
        </Alert>
      )}
      <Button
        onClick={onCreate}
        disabled={creating || multiCurrency || totals.currencies.length === 0 || !account
          || Boolean(blocked)}
        title={blocked || undefined}
      >
        {creating ? 'Creating…' : `Create ${docTypeLabel(docType)}`}
      </Button>
    </div>
  );
}

export default BillingoReviewPanel;
